import { ExpandMore } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Avatar,
  Box,
  Card,
  CardMedia,
  Chip,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import Button from '../../../../design/button/Button';
import Menu from '../../../../design/menu/Menu';
import { useConfirmation } from '../../../../hooks';
import { EikenMaterialResource, type EikenMaterialData } from '../../../../resources';
import { UserRole } from '../../../../resources/BaseResource';
import { formatDate } from '../../../../utils';
import placeholderImage from './placeholder.png';

export function QuestionItem({ label, question }: { label: string; question: string }) {
  if (!label) return null;
  return (
    <Card sx={{ borderRadius: 3, boxShadow: 0, p: 2, bgcolor: 'grey.50' }}>
      <h3 className="text-md font-bold">{label}</h3>
      <p className="text-sm text-gray-500 line-clamp-3">{question}</p>
    </Card>
  );
}

export interface EikenMaterialItemProps {
  item: EikenMaterialData;
  onDeleteSuccess?: VoidFunction;
}

export function EikenMaterialItem({ item, onDeleteSuccess }: EikenMaterialItemProps) {
  const { user } = useAuth();
  const isAdmin = user?.data.role === UserRole.Admin;

  const navigate = useNavigate();
  const deleteConfirmation = useConfirmation<EikenMaterialData>({
    getTitle: (item) => `Delete "${item?.referenceTextTitle}" Question`,
    getDescription: () => `Are you sure you want to delete Question?`,
    onConfirm: async (item) => {
      await EikenMaterialResource.deleteOne(item.id);
      onDeleteSuccess?.();
    },
  });
  const [expanded, setExpanded] = useState(false);
  const itemInitials =
    item.referenceTextTitle && item.referenceTextTitle.length > 0
      ? item.referenceTextTitle
          .split(' ')
          .slice(0, 2)
          .map((name) => name[0].toLocaleUpperCase())
          .join('')
      : '?';

  const isEmpty = Object.values(item.content).every((question) => question === '');

  const [isPublished, setIsPublished] = useState(item.isPublished);
  const handlePublishedChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    await EikenMaterialResource.updateOne(item.id, { isPublished: !isPublished });
    setIsPublished(!isPublished);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{ borderRadius: 3, boxShadow: 1, p: 2, bgcolor: 'white' }}
    >
      <div className="flex flex-1 flex-row w-full">
        <div className="flex flex-1 flex-row items-center gap-2">
          <Box width={'25%'} display="flex" alignItems="center" gap={1}>
            <Avatar
              sx={{
                bgcolor: 'border.primary',
                width: 48,
                height: 48,
                fontWeight: 700,
                fontSize: 20,
              }}
            >
              {itemInitials}
            </Avatar>
            <Box flex={1}>
              <Typography fontWeight={700}>{item.referenceTextTitle || 'Untitled'}</Typography>
            </Box>
          </Box>
          <Box width={'15%'}>
            <ImagePreview item={item} />
          </Box>
          <Box width={'15%'}>
            <Typography fontSize={13} color="text.secondary">
              {item.createdAt && formatDate(item.createdAt)}
            </Typography>
          </Box>
          <Box width={'20%'} display="flex" alignItems="center" gap={1}>
            {item?.createdBy && (
              <>
                {item.createdBy.avatar ? (
                  <Avatar src={item.createdBy.avatar} sx={{ width: 36, height: 36 }} />
                ) : (
                  <Avatar
                    sx={{ width: 36, height: 36, bgcolor: 'grey.300', color: 'text.primary' }}
                  >
                    {item.createdBy.name[0]}
                  </Avatar>
                )}
                <Typography fontSize={14} className="truncate">
                  {item.createdBy.name}
                </Typography>
              </>
            )}
          </Box>
          <Box width={'15%'} textAlign="center">
            {item?.company && (
              <Chip
                label={item.company.title}
                color="primary"
                size="small"
                sx={{ bgcolor: 'border.primary' }}
              />
            )}
          </Box>
          <Box width="10%" className="flex justify-center items-center">
            {isAdmin ? (
              <Switch
                disabled={!isPublished && !item.image}
                checked={isPublished}
                onChange={handlePublishedChange}
              />
            ) : (
              <Tooltip title={isPublished ? 'Published' : 'Not Published'}>
                <div
                  className={`w-6 h-6 rounded-full ${isPublished ? 'bg-green-500' : 'bg-gray-200'}`}
                />
              </Tooltip>
            )}
          </Box>
        </div>
        <div className="flex items-center gap-1">
          <Menu
            anchor={<Button label={<MoreVertIcon />} variant="text" sx={{ minWidth: 0, p: 1 }} />}
            items={[
              {
                name: 'edit',
                label: 'Edit',
                onSelect: () => {
                  if (item.imagePrompt) {
                    navigate(`/eiken-materials/update-generated?id=${item.id}`);
                  } else {
                    navigate(`/eiken-materials/update-manual?id=${item.id}`);
                  }
                },
              },
              {
                name: 'delete',
                label: 'Delete',
                onSelect: () => deleteConfirmation.triggerFor(item),
              },
            ]}
          />
          <IconButton disabled={isEmpty} size="small" onClick={() => setExpanded(!expanded)}>
            <ExpandMore
              sx={{
                transition: 'transform 0.3s ease-in-out',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </IconButton>
        </div>
      </div>
      {expanded && (
        <div className="grid grid-cols-5 gap-2 mt-5" style={{ animation: 'fadeIn 0.3s ease-in' }}>
          <QuestionItem label="Question 1" question={item.content.q1} />
          <QuestionItem label="Question 2" question={item.content.q2} />
          <QuestionItem label="Question 3" question={item.content.q3} />
          <QuestionItem label="Question 4" question={item.content.q4} />
          <QuestionItem label="Pronunciation" question={item.referenceText} />
        </div>
      )}
    </Box>
  );
}

const ImageOverlay = ({ message }: { message: string }) => (
  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-md">
    <Typography fontSize={13} className="text-white">
      {message}
    </Typography>
  </div>
);

const ImagePreview = ({ item }: { item: EikenMaterialData }) => {
  // manual materials
  if (item.generatedImageStatus == null) {
    return (
      <div className="relative h-[60px] w-[120px]">
        <CardMedia
          component="img"
          image={item.image || placeholderImage}
          alt={item.referenceTextTitle}
          sx={{
            aspectRatio: 2 / 1,
            borderRadius: 2,
            height: '60px',
            width: '120px',
            objectFit: 'cover',
          }}
        />
        {!item.image && <ImageOverlay message="No Image" />}
      </div>
    );
  }

  // generated materials
  if (!item.image) {
    return (
      <div className="relative h-[60px] w-[120px]">
        <CardMedia
          component="img"
          image={item.generatedImage || placeholderImage}
          alt={item.referenceTextTitle}
          sx={{
            aspectRatio: 2 / 1,
            borderRadius: 2,
            height: '60px',
            width: '120px',
            objectFit: 'cover',
          }}
        />
        {['active'].includes(item.imageGenerationStatus || '') ? (
          <ImageOverlay message="Generating..." />
        ) : (
          !item.image && <ImageOverlay message="Awaits Publishing" />
        )}
      </div>
    );
  }

  return (
    <div className="relative h-[60px] w-[120px]">
      {item.image && (
        <CardMedia
          component="img"
          image={item.image}
          alt={item.referenceTextTitle}
          sx={{
            aspectRatio: 2 / 1,
            borderRadius: 2,
            height: '60px',
            width: '120px',
            objectFit: 'cover',
          }}
        />
      )}
    </div>
  );
};
